/**
 * /investigations — session log of past investigations.
 *
 * Layout (top → bottom):
 *   1. Breadcrumb-style page header with total count.
 *   2. Filter bar: status chips (All/Running/Done/Failed), author Autocomplete,
 *      search box (debounced 300ms), Mine-only switch. State persists in the
 *      URL via useSearchParams so refresh + back-button restore the view.
 *   3. Session-log list — pure <Box> map, one row per investigation. Each
 *      row carries an avatar + name cell so triagers can see who ran what.
 *   4. Empty state — distinguishes "no investigations yet" from "no rows
 *      match these filters" and offers a Clear-filters CTA in the latter case.
 *
 * Wire conventions:
 *   - Params are serialized to the URL only when non-default. `status=all` is
 *     the absence of a status param; `mine=true` toggles the boolean param.
 *   - The author Autocomplete is greyed out when Mine-only is on (the
 *     backend will overlap them anyway — surface that to the user).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  InputAdornment,
  Pagination,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';

import { investigate } from '@/helpers/ApiHelper';
import { ApiError } from '@/helpers/ApiHelper';
import { useAuth } from '@/contexts/AuthContext';
import { isAdminEmail } from '@/auth/admin';
import { formatRelative } from '@/utils/formatRelative';
import type {
  InvestigationAnchor,
  InvestigationAuthor,
  InvestigationStatus,
  InvestigationSummary,
  InvestigationUser,
  PaginatedInvestigations,
} from '@/types/investigation';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

type StatusFilter = 'all' | InvestigationStatus;
type DeletedFilter = 'active' | 'deleted' | 'all';

const STATUS_CHIPS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'running', label: 'Running' },
  { value: 'done', label: 'Done' },
  { value: 'failed', label: 'Failed' },
];

// ─── URL state helpers ───────────────────────────────────────────────────────

interface FilterState {
  page: number;
  status: StatusFilter;
  user_id: string;
  q: string;
  mine: boolean;
  // Admin-only: visible/deleted/all. Persisted in URL as ?deleted=.
  deleted: DeletedFilter;
}

function readFiltersFromUrl(params: URLSearchParams): FilterState {
  const status = params.get('status');
  const deleted = params.get('deleted');
  return {
    page: Math.max(1, Number(params.get('page')) || 1),
    status:
      status === 'running' || status === 'done' || status === 'failed' ? status : 'all',
    user_id: params.get('user_id') ?? '',
    q: params.get('q') ?? '',
    mine: params.get('mine') === 'true',
    deleted:
      deleted === 'deleted' || deleted === 'all' ? deleted : 'active',
  };
}

function filtersToUrl(state: FilterState): URLSearchParams {
  const next = new URLSearchParams();
  if (state.page > 1) next.set('page', String(state.page));
  if (state.status !== 'all') next.set('status', state.status);
  if (state.user_id) next.set('user_id', state.user_id);
  if (state.q) next.set('q', state.q);
  if (state.mine) next.set('mine', 'true');
  // Only persist non-default deleted filter in the URL.
  if (state.deleted !== 'active') next.set('deleted', state.deleted);
  return next;
}

function hasActiveFilters(state: FilterState): boolean {
  return (
    state.status !== 'all' ||
    state.user_id !== '' ||
    state.q !== '' ||
    state.mine
  );
}

// ─── Status chip ─────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: InvestigationSummary['status'] }): React.ReactElement {
  const map: Record<
    InvestigationSummary['status'],
    { color: 'default' | 'success' | 'error' | 'warning'; label: string }
  > = {
    running: { color: 'warning', label: 'running' },
    done: { color: 'success', label: 'done' },
    failed: { color: 'error', label: 'failed' },
  };
  const { color, label } = map[status];
  return (
    <Chip
      size="small"
      color={color}
      label={label}
      variant="outlined"
      sx={{
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '0.625rem',
        height: 20,
        flexShrink: 0,
      }}
    />
  );
}

// ─── Author cell ─────────────────────────────────────────────────────────────

function displayNameFor(user: InvestigationUser): string {
  // Prefer the first name token, fall back to the email local-part. Keeps
  // rows tight — we have at most ~80px of horizontal real estate.
  if (user.name) {
    const first = user.name.split(/\s+/)[0];
    if (first) return first;
  }
  const local = user.email.split('@')[0];
  return local ?? user.email;
}

function monogramFor(user: InvestigationUser): string {
  const source = user.name || user.email;
  return source.slice(0, 1).toUpperCase();
}

function AuthorCell({
  user,
  compact = false,
}: {
  user: InvestigationUser;
  compact?: boolean;
}): React.ReactElement {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{ flexShrink: 0, minWidth: 0 }}
    >
      <Avatar
        src={user.picture_url || undefined}
        alt={user.name || user.email}
        sx={{
          width: 22,
          height: 22,
          borderRadius: 0.5,
          backgroundColor: 'primary.soft',
          color: 'primary.main',
          border: '1px solid',
          borderColor: 'primary.strong',
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {monogramFor(user)}
      </Avatar>
      {!compact && (
        <Typography
          component="span"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '0.75rem',
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 100,
          }}
        >
          {displayNameFor(user)}
        </Typography>
      )}
    </Stack>
  );
}

// ─── Anchor window text ───────────────────────────────────────────────────────

function asAnchor(raw: InvestigationSummary['anchor']): InvestigationAnchor | null {
  if (!raw || typeof raw !== 'object') return null;
  const anchor = raw as Record<string, unknown>;
  if (
    typeof anchor.from_ts !== 'string' ||
    typeof anchor.to_ts !== 'string' ||
    !Array.isArray(anchor.services) ||
    !Array.isArray(anchor.symptoms) ||
    typeof anchor.raw_prompt !== 'string'
  ) {
    return null;
  }
  return anchor as unknown as InvestigationAnchor;
}

function formatAnchorWindow(anchor: InvestigationAnchor | null): string {
  if (!anchor) return '—';
  const fmt = (iso: string): string => {
    try {
      const d = new Date(iso);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return iso;
    }
  };
  const toLabel = anchor.to_ts ? fmt(anchor.to_ts) : 'now';
  return `${fmt(anchor.from_ts)} → ${toLabel}`;
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function SessionRow({
  row,
  isLast,
  isAdmin = false,
}: {
  row: InvestigationSummary;
  isLast: boolean;
  isAdmin?: boolean;
}): React.ReactElement {
  const anchor = asAnchor(row.anchor);
  const isFollowup = row.parent_id !== null && row.parent_id !== undefined;
  const isDeleted = Boolean(row.deleted_at);
  const shortId = `#${row.id.slice(0, 4)}`;
  const promptPreview =
    row.prompt.length > 60 ? row.prompt.slice(0, 59) + '…' : row.prompt;
  const costLabel = `$${row.cost_usd.toFixed(2)}`;
  const latencyLabel = `${(row.latency_ms / 1000).toFixed(1)}s`;
  const anchorLabel = formatAnchorWindow(anchor);

  // Deleted row tooltip — relative time of deletion.
  const deletedTooltip =
    isAdmin && isDeleted && row.deleted_at
      ? `Deleted at ${formatRelative(row.deleted_at)}`
      : '';

  const rowContent = (
    <Box
      component={RouterLink}
      to={`/investigations/${row.id}`}
      aria-label={`Investigation ${shortId} by ${row.user.name || row.user.email}: ${promptPreview}${isDeleted ? ' (deleted)' : ''}`}
      sx={{
        display: 'block',
        py: 1.5,
        pr: 2,
        pl: isFollowup ? 4 : 2,
        color: 'text.primary',
        textDecoration: 'none',
        borderBottom: isLast ? 0 : 1,
        borderColor: 'divider',
        borderLeft: '2px solid transparent',
        transition: 'border-left-color 120ms ease, background-color 120ms ease',
        // Deleted rows: lower opacity + slightly different hover treatment.
        opacity: isAdmin && isDeleted ? 0.6 : 1,
        '&:hover': {
          borderLeftColor: isAdmin && isDeleted ? 'error.main' : 'primary.main',
          backgroundColor: 'rgba(255,255,255,0.02)',
        },
      }}
    >
      {/* Desktop: single-line layout */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 2,
        }}
      >
        {isFollowup && (
          <KeyboardArrowRightIcon
            fontSize="small"
            sx={{ color: 'text.secondary', flexShrink: 0 }}
            aria-label="Follow-up investigation"
          />
        )}

        <Typography
          component="span"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.75rem',
            fontWeight: 700,
            color: 'primary.main',
            flexShrink: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {shortId}
        </Typography>

        <Typography
          component="span"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6875rem',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
            color: (theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled,
            flexShrink: 0,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {anchorLabel}
        </Typography>

        <StatusChip status={row.status} />

        {/* Deleted pill — admin view only */}
        {isAdmin && isDeleted && (
          <Chip
            size="small"
            label="deleted"
            color="error"
            variant="outlined"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.625rem',
              height: 20,
              flexShrink: 0,
            }}
          />
        )}

        {/* Author cell — avatar + first-name. Sits between status and cost
            so the page's "what / when / who / how-much" rhythm is intact. */}
        <AuthorCell user={row.user} />

        <Typography
          component="span"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6875rem',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
            color: (theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled,
            flexShrink: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {costLabel}
        </Typography>

        <Typography
          component="span"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6875rem',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
            color: (theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled,
            flexShrink: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {latencyLabel}
        </Typography>

        <Typography
          component="span"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '0.8125rem',
            color: 'text.primary',
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            // Strikethrough on prompt text for deleted rows (admin view).
            textDecoration: isAdmin && isDeleted ? 'line-through' : 'none',
          }}
        >
          {promptPreview}
        </Typography>
      </Box>

      {/* Mobile: stacked card layout */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          {isFollowup && (
            <KeyboardArrowRightIcon
              fontSize="small"
              sx={{ color: 'text.secondary', flexShrink: 0 }}
              aria-hidden="true"
            />
          )}
          <Typography
            component="span"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: 'primary.main',
            }}
          >
            {shortId}
          </Typography>
          <StatusChip status={row.status} />
          {isAdmin && isDeleted && (
            <Chip
              size="small"
              label="deleted"
              color="error"
              variant="outlined"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.625rem',
                height: 20,
                flexShrink: 0,
              }}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <AuthorCell user={row.user} compact />
        </Stack>

        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6875rem',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
            color: (theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled,
            mb: 0.5,
          }}
        >
          {anchorLabel} · {costLabel} · {latencyLabel}
        </Typography>

        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '0.8125rem',
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: isAdmin && isDeleted ? 'line-through' : 'none',
          }}
        >
          {promptPreview}
        </Typography>
      </Box>
    </Box>
  );

  // Wrap deleted rows with a tooltip when admin can see deletion time.
  if (deletedTooltip) {
    return <Tooltip title={deletedTooltip} placement="left">{rowContent}</Tooltip>;
  }
  return rowContent;
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

function FilterBar({
  state,
  authors,
  authorsLoading,
  searchInput,
  onSearchChange,
  onPatch,
  isAdmin = false,
}: {
  state: FilterState;
  authors: InvestigationAuthor[];
  authorsLoading: boolean;
  searchInput: string;
  onSearchChange: (value: string) => void;
  onPatch: (patch: Partial<FilterState>) => void;
  isAdmin?: boolean;
}): React.ReactElement {
  const selectedAuthor = useMemo(
    () => authors.find((a) => a.id === state.user_id) ?? null,
    [authors, state.user_id],
  );
  const authorPickerDisabled = state.mine;

  return (
    <Stack spacing={1.5} sx={{ mb: 1 }}>
      {/* Row 1: status chips + mine switch */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1.5, sm: 2 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexWrap="wrap"
        useFlexGap
      >
        <Stack
          direction="row"
          spacing={1}
          role="radiogroup"
          aria-label="Filter investigations by status"
          flexWrap="wrap"
          useFlexGap
        >
          {STATUS_CHIPS.map((chip) => {
            const active = state.status === chip.value;
            return (
              <Chip
                key={chip.value}
                label={chip.label}
                role="radio"
                aria-checked={active}
                clickable
                onClick={() => onPatch({ status: chip.value, page: 1 })}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  minHeight: 32,
                  ...(active
                    ? {
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(217, 119, 87, 0.10)',
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'rgba(217, 119, 87, 0.16)' },
                        '& .MuiChip-label': { color: 'primary.main' },
                      }
                    : {
                        borderColor: 'divider',
                        color: 'text.secondary',
                        '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                      }),
                }}
              />
            );
          })}
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={state.mine}
              onChange={(_, checked) => onPatch({ mine: checked, page: 1 })}
              inputProps={{ 'aria-label': 'Mine only' }}
            />
          }
          label={
            <Typography
              component="span"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: state.mine ? 'primary.main' : 'text.secondary',
              }}
            >
              Mine only
            </Typography>
          }
        />
      </Stack>

      {/* Row 1b: admin deleted filter (only visible to mohit) */}
      {isAdmin && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography
            component="span"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.secondary',
              flexShrink: 0,
            }}
          >
            Show
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={state.deleted}
            exclusive
            onChange={(_, value: DeletedFilter | null) => {
              if (value !== null) onPatch({ deleted: value, page: 1 });
            }}
            aria-label="Show active, deleted, or all investigations"
          >
            <ToggleButton
              value="active"
              aria-label="Show active investigations"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                minHeight: 32,
                px: 1.5,
              }}
            >
              Active
            </ToggleButton>
            <ToggleButton
              value="deleted"
              aria-label="Show deleted investigations"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                minHeight: 32,
                px: 1.5,
                '&.Mui-selected': { color: 'error.main' },
              }}
            >
              Deleted
            </ToggleButton>
            <ToggleButton
              value="all"
              aria-label="Show all investigations"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                minHeight: 32,
                px: 1.5,
              }}
            >
              All
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}

      {/* Row 2: search + author picker */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems="stretch"
      >
        <TextField
          size="small"
          placeholder="Search prompt, name, or email…"
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          inputProps={{ 'aria-label': 'Search investigations' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  fontSize="small"
                  sx={{ color: 'text.secondary' }}
                  aria-hidden="true"
                />
              </InputAdornment>
            ),
          }}
          sx={{
            flexGrow: 1,
            minWidth: { xs: '100%', sm: 280 },
            '& .MuiOutlinedInput-input': {
              fontFamily: '"Inter", sans-serif',
              fontSize: 13,
            },
          }}
        />

        <Autocomplete
          size="small"
          options={authors}
          loading={authorsLoading}
          value={selectedAuthor}
          disabled={authorPickerDisabled}
          onChange={(_, value) => onPatch({ user_id: value?.id ?? '', page: 1 })}
          getOptionLabel={(option) => option.name || option.email}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={option.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Avatar
                src={option.picture_url || undefined}
                alt={option.name || option.email}
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: 0.5,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {(option.name || option.email).slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  component="span"
                  sx={{ fontFamily: '"Inter", sans-serif', fontSize: 13 }}
                >
                  {option.name || option.email.split('@')[0]}
                </Typography>
              </Box>
              <Typography
                component="span"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 11,
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              >
                {option.investigation_count}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={authorPickerDisabled ? 'Mine only is on' : 'Filter by author'}
              inputProps={{
                ...params.inputProps,
                'aria-label': 'Filter by author',
              }}
              sx={{
                '& .MuiOutlinedInput-input': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: 13,
                },
              }}
            />
          )}
          sx={{ minWidth: { xs: '100%', sm: 240 } }}
        />
      </Stack>
    </Stack>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function InvestigationsListPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => readFiltersFromUrl(searchParams), [searchParams]);
  const { user } = useAuth();
  const isAdmin = isAdminEmail(user?.email);

  // Search input is kept local for snappy typing, then debounced into the
  // URL state. Keeps the URL stable while the user types.
  const [searchInput, setSearchInput] = useState(state.q);

  const [data, setData] = useState<PaginatedInvestigations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [authors, setAuthors] = useState<InvestigationAuthor[]>([]);
  const [authorsLoading, setAuthorsLoading] = useState(true);

  // Patch URL state. Page resets are caller-supplied because a status change
  // resets to page 1 but a paginator click doesn't.
  const patchState = (patch: Partial<FilterState>): void => {
    const next: FilterState = { ...state, ...patch };
    setSearchParams(filtersToUrl(next), { replace: true });
  };

  // Debounce the search box → URL state. Fires one request per pause.
  useEffect(() => {
    if (searchInput === state.q) return;
    const handle = setTimeout(() => {
      patchState({ q: searchInput, page: 1 });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- patchState identity-stable; state.q is the source of truth
  }, [searchInput]);

  // If the URL q changes via back-button (or Clear filters), sync local input.
  useEffect(() => {
    if (state.q !== searchInput) {
      setSearchInput(state.q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-way sync from URL → local input
  }, [state.q]);

  // Fetch authors once on mount. The list is bounded by team size.
  useEffect(() => {
    let active = true;
    setAuthorsLoading(true);
    investigate
      .listAuthors()
      .then((resp) => {
        if (!active) return;
        setAuthors(resp.results);
      })
      .catch(() => {
        // Author picker failure is non-fatal — the rest of the page works.
        if (!active) return;
        setAuthors([]);
      })
      .finally(() => {
        if (!active) return;
        setAuthorsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Fetch the list whenever URL-driven filter state changes.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    investigate
      .list({
        page: state.page,
        status: state.status === 'all' ? undefined : state.status,
        user_id: state.user_id || undefined,
        q: state.q || undefined,
        mine: state.mine || undefined,
        // For non-admin users the backend silently coerces this to "active",
        // so it's safe to always send it.
        deleted: state.deleted,
      })
      .then((resp) => {
        if (!active) return;
        setData(resp);
      })
      .catch((err: unknown) => {
        if (!active) return;
        const msg =
          err instanceof ApiError
            ? err.detail
            : err instanceof Error
              ? err.message
              : 'Unknown error';
        setError(msg);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [state.page, state.status, state.user_id, state.q, state.mine, state.deleted]);

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;
  const filtersActive = hasActiveFilters(state);

  const clearFilters = (): void => {
    setSearchInput('');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={2}>
        {/* Page header */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.6875rem',
                fontWeight: 400,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'text.secondary',
                display: 'block',
                mb: 0.25,
              }}
            >
              ▸ SESSION LOG
            </Typography>
            {data && (
              <Typography
                variant="caption"
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
                color={(theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled}
                sx={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                {data.count} investigation{data.count !== 1 ? 's' : ''}
                {filtersActive ? ' · filtered' : ''}
              </Typography>
            )}
          </Box>
        </Stack>

        <FilterBar
          state={state}
          authors={authors}
          authorsLoading={authorsLoading}
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          onPatch={patchState}
          isAdmin={isAdmin}
        />

        {error && (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        )}

        {loading && !data ? (
          <Box
            role="status"
            aria-label="Loading investigations"
            sx={{ display: 'flex', justifyContent: 'center', py: 6 }}
          >
            <CircularProgress />
          </Box>
        ) : data && data.results.length === 0 ? (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.8125rem',
                color: 'text.secondary',
              }}
            >
              {filtersActive
                ? 'no investigations match these filters'
                : 'no investigations yet'}
            </Typography>
            {filtersActive ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 1,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.75rem',
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={clearFilters}
                  aria-label="Clear all filters"
                  sx={{
                    background: 'none',
                    border: 'none',
                    color: 'primary.main',
                    cursor: 'pointer',
                    padding: 0,
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    textDecoration: 'underline',
                    '&:hover': { color: 'primary.dark' },
                  }}
                >
                  clear filters
                </Box>{' '}
                to see every investigation
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 1,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.75rem',
                }}
              >
                start one from the{' '}
                <RouterLink to="/investigate" style={{ color: 'inherit' }}>
                  investigate
                </RouterLink>{' '}
                page
              </Typography>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            {data?.results.map((row, idx) => (
              <SessionRow
                key={row.id}
                row={row}
                isLast={idx === (data?.results.length ?? 0) - 1}
                isAdmin={isAdmin}
              />
            ))}
          </Box>
        )}

        {data && data.count > PAGE_SIZE && (
          <>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <Pagination
                count={totalPages}
                page={state.page}
                onChange={(_, p) => patchState({ page: p })}
                color="primary"
                shape="rounded"
                size="small"
              />
            </Box>
          </>
        )}
      </Stack>
    </Container>
  );
}
